import { useState, useEffect, useContext, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Navbar from '../components/Navbar';
import ShareButton from '../components/ShareButton';
import { Button } from '../components/ui/button';
import { 
  Mic, MicOff, Users, Clock, Calendar, Play, Radio, 
  Share2, Headphones, ChevronRight, 
  Sparkles, BookOpen, Briefcase, HelpCircle, Filter,
  PhoneOff, Loader2, User, Hand, UserPlus, Volume2
} from 'lucide-react';
import { toast } from 'sonner';
import { useWebRTC } from '../components/webrtc/LiveKitContext';
import SessionEndedModal from '../components/classroom/SessionEndedModal';
import { parseEAT } from '../utils/eatTime';

// Hidden audio element — plays a remote WebRTC audio stream.
// Mirrors the ClassroomSession `RemoteAudioPlayer`: handles
// • srcObject churn (only re-assigns when the stream actually changes)
// • autoplay being silently blocked on iOS Safari / Chrome mobile (retries
//   play() on first user gesture)
// • late-arriving tracks (host unmutes after student joined — addtrack
//   listener retries play() so the new track actually starts).
function AudioPlayer({ stream }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !stream) return;
    if (el.srcObject !== stream) el.srcObject = stream;
    el.muted = false;
    el.volume = 1.0;
    const tryPlay = () => {
      const p = el.play?.();
      if (p && typeof p.then === 'function') {
        p.catch(() => {
          const retry = () => {
            el.play?.().catch(() => {});
            document.removeEventListener('click', retry, true);
            document.removeEventListener('touchstart', retry, true);
            document.removeEventListener('keydown', retry, true);
          };
          document.addEventListener('click', retry, true);
          document.addEventListener('touchstart', retry, true);
          document.addEventListener('keydown', retry, true);
        });
      }
    };
    tryPlay();
    stream.addEventListener?.('addtrack', tryPlay);
    return () => { stream.removeEventListener?.('addtrack', tryPlay); };
  }, [stream]);
  return <audio ref={ref} autoPlay playsInline />;
}

function AudioRooms() {
  const { user, login } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [joiningRoomId, setJoiningRoomId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Lobby state: a room the user is "waiting" in (host hasn't started yet).
  // We poll the room every ~3s and auto-join LiveKit the moment status flips
  // to `live`, so the host doesn't have to tell listeners to refresh.
  const [lobbyRoom, setLobbyRoom] = useState(null);
  const autoJoinedRef = useRef(false);
  const isMobileWebView = searchParams.get('mobile') === 'true';

  // Handle mobile WebView token auth
  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (urlToken && !localStorage.getItem('token')) {
      localStorage.setItem('token', urlToken);
      // Trigger re-auth by reloading user from token
      if (login) {
        axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${urlToken}` } })
          .then(res => { if (res.data) login(urlToken, res.data); })
          .catch(() => {});
      }
    }
  }, []);

  const {
    joinRoom: joinRoom_webrtc, leaveRoom, isJoined, isAudioEnabled, toggleAudio,
    participants, toggleHandRaise, remoteStreams, raisedHands,
    forceMuteParticipant, kickParticipant, endSession,
    registerSessionEndedHandler,
  } = useWebRTC();

  const [sessionEnded, setSessionEnded] = useState(null);

  // Show a clear modal when the host ends the room instead of a silent drop.
  useEffect(() => {
    if (!registerSessionEndedHandler) return;
    registerSessionEndedHandler((data) => {
      setSessionEnded({
        message: data?.message || 'The host has ended the audio room.',
        endedBy: data?.ended_by,
      });
    });
    return () => registerSessionEndedHandler(null);
  }, [registerSessionEndedHandler]);

  const loadRooms = async () => {
    setLoading(true);
    try {
      let url = `${API}/audio-rooms`;
      const params = [];
      if (activeFilter !== 'all') params.push(`category=${activeFilter}`);
      if (params.length > 0) url += '?' + params.join('&');
      const res = await axios.get(url);
      setRooms(res.data);
    } catch (err) {
      console.error('Failed to load rooms:', err);
    }
    setLoading(false);
  };

  useEffect(() => { loadRooms(); }, [activeFilter]);

  // Auto-join from URL param
  useEffect(() => {
    if (autoJoinedRef.current || loading || rooms.length === 0) return;
    const joinId = searchParams.get('join');
    if (joinId && user) {
      const room = rooms.find(r => r.id === joinId);
      if (room && room.status === 'live') {
        autoJoinedRef.current = true;
        handleJoinRoom(room);
      } else if (room) {
        toast.info('This room is not live yet');
        setSearchParams({});
      }
    }
  }, [rooms, loading, user]);

  // Deep-link scroll + highlight. When someone opens a shared room link
  // (`/audio-rooms?focus=<id>`), we land them on the list, scroll the
  // specific card into view, pulse it briefly, and surface a hint — even
  // for anonymous recipients so they immediately see WHICH room was shared
  // and the next step. The handler only fires once per visit.
  const focusHandledRef = useRef(false);
  const [focusedRoomId, setFocusedRoomId] = useState(null);
  useEffect(() => {
    if (focusHandledRef.current || loading || rooms.length === 0) return;
    const focusId = searchParams.get('focus');
    if (!focusId) return;
    const room = rooms.find(r => r.id === focusId);
    if (!room) {
      toast.info('That room is no longer available');
      setSearchParams({});
      focusHandledRef.current = true;
      return;
    }
    focusHandledRef.current = true;
    setFocusedRoomId(focusId);
    // Scroll after paint so the card has mounted.
    setTimeout(() => {
      const el = document.getElementById(`room-${focusId}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 250);
    if (!user) {
      toast.info(`Sign in to join "${room.title || 'this room'}"`, { duration: 5000 });
    } else {
      const status = (room.status || '').toLowerCase();
      if (status === 'live') {
        toast.success(`"${room.title || 'Room'}" is live now — tap Join`, { duration: 5000 });
      } else if (status === 'scheduled') {
        toast.info(`"${room.title || 'Room'}" starts soon — tap Join to wait in the lobby`, { duration: 5000 });
      } else {
        toast.info(`"${room.title || 'This room'}" is no longer live`, { duration: 5000 });
      }
    }
    // Clear the highlight after a few seconds — URL param stays so the
    // user can refresh and the deeplink still works for returning visits.
    setTimeout(() => setFocusedRoomId(null), 4500);
  }, [rooms, loading, user, searchParams, setSearchParams]);

  // Host starts the room (sets it live)
  const handleStartRoom = async (room) => {
    setJoiningRoomId(room.id);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/audio-rooms/${room.id}/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Now join LiveKit
      const roomId = `audio_${room.id}`;
      await joinRoom_webrtc(roomId, user.id, user.name || 'Host', 'teacher', { audioOnly: true });
      const freshRoom = await axios.get(`${API}/audio-rooms/${room.id}`);
      setSelectedRoom(freshRoom.data);
      toast.success('Room is live!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start room');
    } finally {
      setJoiningRoomId(null);
      if (searchParams.get('join')) setSearchParams({});
    }
  };

  // Guest joins a live room. If the host hasn't started yet (`scheduled`),
  // put the guest into a lobby and auto-admit when the room goes live.
  const handleJoinRoom = async (room) => {
    if (!user) { toast.error('Please login to join'); return; }
    if (joiningRoomId) return;

    const status = (room.status || '').toLowerCase();
    if (status !== 'live') {
      // Lobby path — host hasn't tapped "Start" yet. Register in the lobby
      // list (best effort) so the host sees how many people are waiting,
      // then poll until the room goes live.
      setLobbyRoom(room);
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/audio-rooms/${room.id}/lobby`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {}); // non-fatal — endpoint may not exist on older backends
      } catch { /* ignore */ }
      toast.info(`Waiting for ${room.host_name || 'the host'} to start the room…`);
      return;
    }

    setJoiningRoomId(room.id);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/audio-rooms/${room.id}/join`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const roomId = `audio_${room.id}`;
      const isHost = room.host_id === user.id;
      await joinRoom_webrtc(roomId, user.id, user.name || 'User', isHost ? 'teacher' : 'student', { audioOnly: true });
      const freshRoom = await axios.get(`${API}/audio-rooms/${room.id}`);
      setSelectedRoom(freshRoom.data);
      setLobbyRoom(null);
      toast.success('Joined room!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join room');
    } finally {
      setJoiningRoomId(null);
      if (searchParams.get('join')) setSearchParams({});
    }
  };

  const leaveLobby = () => {
    const r = lobbyRoom;
    setLobbyRoom(null);
    if (!r) return;
    const token = localStorage.getItem('token');
    axios.post(`${API}/audio-rooms/${r.id}/lobby/leave`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    }).catch(() => {});
  };

  // Lobby auto-admit: while a user is in the lobby, poll the room every 3s.
  // The moment status flips to `live`, auto-call handleJoinRoom with the
  // fresh room doc. Also surface lobby count + reflect host actions
  // ("rescheduled", "cancelled") so the user isn't stuck forever.
  useEffect(() => {
    if (!lobbyRoom) return;
    let cancelled = false;
    const token = localStorage.getItem('token');

    const beat = async () => {
      try {
        // Best-effort lobby heartbeat so the host sees waiter count
        axios.post(`${API}/audio-rooms/${lobbyRoom.id}/lobby/heartbeat`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {});
        const res = await axios.get(`${API}/audio-rooms/${lobbyRoom.id}`);
        if (cancelled) return;
        const fresh = res.data;
        const s = (fresh.status || '').toLowerCase();
        setLobbyRoom((prev) => prev ? { ...prev, ...fresh } : prev);
        if (s === 'live') {
          cancelled = true;
          handleJoinRoom(fresh);
        } else if (['ended', 'completed', 'cancelled', 'expired'].includes(s)) {
          cancelled = true;
          toast.info('The host closed this room before starting.');
          setLobbyRoom(null);
        }
      } catch { /* ignore transient errors */ }
    };

    beat();
    const iv = setInterval(beat, 3000);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lobbyRoom?.id]);

  // One-tap "reschedule for tomorrow" — host (or admin) recovers a room
  // that wasn't started in time. Backend defaults preset to tomorrow_same_time.
  const handleRescheduleTomorrow = async (room) => {
    setJoiningRoomId(room.id);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/audio-rooms/${room.id}/reschedule`,
        { preset: 'tomorrow_same_time' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Rescheduled to ${res.data.scheduled_date} at ${res.data.scheduled_time}`);
      setRooms(prev => prev.map(r => r.id === room.id
        ? { ...r, status: 'scheduled', scheduled_date: res.data.scheduled_date, scheduled_time: res.data.scheduled_time, was_held: false }
        : r));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reschedule');
    } finally {
      setJoiningRoomId(null);
    }
  };

  const handleLeaveRoom = async () => {
    if (selectedRoom) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/audio-rooms/${selectedRoom.id}/leave`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch {}
    }
    leaveRoom();
    setSelectedRoom(null);
    loadRooms();
  };

  const handleEndRoom = async () => {
    if (!selectedRoom) return;
    // End session for all participants
    endSession('Room ended by host');
    await new Promise(r => setTimeout(r, 500));
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/audio-rooms/${selectedRoom.id}/end`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch {}
    leaveRoom();
    setSelectedRoom(null);
    toast.success('Room ended for all participants');
    loadRooms();
  };

  const handleRaiseHand = async () => {
    if (selectedRoom) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/audio-rooms/${selectedRoom.id}/raise-hand`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toggleHandRaise(true);
        toast.info('Hand raised! Waiting for host to invite you to speak.');
      } catch (err) {
        toast.error('Failed to raise hand');
      }
    }
  };

  const handleInviteSpeaker = async (targetUserId) => {
    if (selectedRoom) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/audio-rooms/${selectedRoom.id}/invite-speaker/${targetUserId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Invited to speak');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to invite');
      }
    }
  };

  const filters = [
    { id: 'all', label: 'All', icon: Sparkles },
    { id: 'education', label: 'Education', icon: BookOpen },
    { id: 'q&a', label: 'Q&A', icon: HelpCircle },
    { id: 'study-group', label: 'Study Group', icon: Users },
    { id: 'discussion', label: 'Discussion', icon: Radio },
    { id: 'career', label: 'Career', icon: Briefcase },
  ];

  const isUpNext = (room) => {
    if (!room.scheduled_date || !room.scheduled_time || room.status === 'live') return false;
    const scheduledDate = parseEAT(room.scheduled_date, room.scheduled_time);
    if (!scheduledDate) return false;
    const diffMs = scheduledDate - new Date();
    return diffMs > 0 && diffMs < 2 * 60 * 60 * 1000;
  };

  const filteredRooms = rooms.filter(r => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return r.title?.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q) || r.subject?.toLowerCase().includes(q) || r.host_name?.toLowerCase().includes(q);
  });
  const liveRooms = filteredRooms.filter(r => r.status === 'live');
  const upNextRooms = filteredRooms.filter(r => isUpNext(r));
  const scheduledRooms = filteredRooms.filter(r => r.status === 'scheduled' && !isUpNext(r));
  const rescheduledRooms = filteredRooms.filter(r => r.status === 'to_be_rescheduled');
  const completedRooms = filteredRooms.filter(r => r.status === 'completed' || r.status === 'ended');

  // === AUTO-JOIN LOADING STATE ===
  // If URL has ?join= param, show a full-screen loader to prevent listing page flash
  const pendingAutoJoin = searchParams.get('join') && !selectedRoom && !autoJoinedRef.current;
  if (pendingAutoJoin) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center" data-testid="auto-join-loading">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center -mt-16">
          <div className="relative mb-6">
            <div className="w-20 h-20 rounded-full bg-indigo-900/50 border-2 border-indigo-500/30 flex items-center justify-center">
              <Headphones className="w-10 h-10 text-indigo-400" />
            </div>
            <Loader2 className="w-8 h-8 text-indigo-400 animate-spin absolute -bottom-1 -right-1" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Joining Audio Room...</h2>
          <p className="text-sm text-slate-400">Setting up your connection</p>
        </div>
      </div>
    );
  }

  // === ACTIVE ROOM VIEW (Clubhouse style) ===
  if (selectedRoom) {
    const isHost = user && selectedRoom.host_id === user.id;
    // Use WebRTC participants for real-time data (only real connected users)
    const rtcParticipants = participants || [];
    // Separate speakers (teachers/hosts or anyone with audio on) and listeners
    const speakers = rtcParticipants.filter(p => p.role === 'teacher' || p.has_audio);
    const listeners = rtcParticipants.filter(p => p.role !== 'teacher' && !p.has_audio);
    // Use raisedHands from WebRTC context (array of {id, name} objects)
    const handRaisedIds = (raisedHands || []).map(h => h.id || h);

    return (
      <div className="min-h-screen bg-slate-900" data-testid="active-audio-room">
        <Navbar />
        <SessionEndedModal
          open={!!sessionEnded}
          message={sessionEnded?.message}
          endedBy={sessionEnded?.endedBy}
          onClose={() => {
            setSessionEnded(null);
            try { leaveRoom?.(); } catch {}
            setSelectedRoom(null);
          }}
          variant="audio"
          rateContentType={user?.role === 'student' ? 'audio_room' : undefined}
          rateContentId={selectedRoom?.id}
          rateTeacherId={selectedRoom?.host_id}
        />
        <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-6 sm:p-10 border border-slate-700/50 shadow-2xl">
            {/* Room Header */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="flex items-center gap-2 bg-red-500/20 px-4 py-2 rounded-full">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-sm font-bold text-red-400">LIVE</span>
                </div>
                <div className="flex items-center gap-2 bg-slate-700/50 px-4 py-2 rounded-full">
                  <Headphones className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-semibold text-slate-300" data-testid="room-participant-count">
                    {rtcParticipants.length} in room
                  </span>
                </div>
              </div>
              <h1 className="text-2xl sm:text-3xl font-black text-white mb-2" data-testid="room-title">{selectedRoom.title}</h1>
              <p className="text-base text-slate-400">{selectedRoom.topic || selectedRoom.description}</p>
            </div>

            {/* ============ STAGE: Speakers ============ */}
            <div className="relative mb-10">
              {/* soft radial glow behind the stage */}
              <div className="pointer-events-none absolute inset-0 -z-0">
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] rounded-full bg-indigo-500/10 blur-3xl" />
              </div>
              <h3 className="relative text-xs font-bold text-slate-400 uppercase tracking-[0.2em] mb-6 text-center">
                <span className="inline-block px-3 py-1 rounded-full bg-slate-800/80 border border-slate-700/50">
                  On stage · {speakers.length}
                </span>
              </h3>
              <div className="relative flex justify-center gap-x-10 gap-y-8 flex-wrap">
                {speakers.length === 0 && (
                  <p className="text-slate-500 text-sm">Waiting for the first speaker…</p>
                )}
                {speakers.map((p) => {
                  const isMe = p.id === user?.id;
                  const isSpeakerHost = p.id === selectedRoom.host_id;
                  return (
                    <div key={p.id} className="text-center" data-testid={`speaker-${p.id}`}>
                      <div className="relative inline-block mb-3">
                        {/* speaking ring — animates when mic is on */}
                        {p.has_audio && (
                          <>
                            <span className="absolute inset-0 rounded-full ring-4 ring-emerald-400/70 animate-ping" />
                            <span className="absolute inset-0 rounded-full ring-2 ring-emerald-400/80" />
                          </>
                        )}
                        <div className={`relative w-24 h-24 rounded-full flex items-center justify-center shadow-xl ${isSpeakerHost ? 'bg-gradient-to-br from-indigo-500 to-violet-700' : 'bg-gradient-to-br from-cyan-500 to-blue-700'}`}>
                          {p.avatar ? (
                            <img src={p.avatar} alt={p.name} className="w-full h-full rounded-full object-cover" />
                          ) : (
                            <span className="text-white text-2xl font-black">
                              {(p.name || '?').charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className={`absolute -bottom-1 -right-1 w-8 h-8 rounded-full border-[3px] border-slate-900 flex items-center justify-center shadow ${p.has_audio ? 'bg-emerald-500' : 'bg-slate-700'}`}>
                          {p.has_audio ? <Mic className="w-4 h-4 text-white" /> : <MicOff className="w-4 h-4 text-slate-300" />}
                        </div>
                      </div>
                      <p className="text-white font-bold text-sm leading-tight">{p.name}{isMe ? ' (You)' : ''}</p>
                      <p className={`text-[11px] font-semibold uppercase tracking-wider mt-0.5 ${isSpeakerHost ? 'text-indigo-300' : 'text-cyan-300'}`}>
                        {isSpeakerHost ? '★ Host' : 'Speaker'}
                      </p>
                      {isHost && !isMe && (
                        <div className="flex items-center justify-center gap-1.5 mt-2">
                          <button onClick={() => forceMuteParticipant(p.id, p.has_audio)}
                            className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${p.has_audio ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}`}
                            data-testid={`mute-speaker-${p.id}`}>
                            {p.has_audio ? <><MicOff className="w-3 h-3 inline mr-0.5" />Mute</> : <><Mic className="w-3 h-3 inline mr-0.5" />Unmute</>}
                          </button>
                          <button onClick={() => { if (window.confirm(`Remove ${p.name} from the room?`)) kickParticipant(p.id); }}
                            className="text-[10px] bg-red-600 hover:bg-red-700 text-white px-2 py-0.5 rounded-full font-semibold"
                            data-testid={`kick-speaker-${p.id}`}>
                            <PhoneOff className="w-3 h-3 inline mr-0.5" />Remove
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* stage divider */}
            <div className="relative mb-8">
              <div className="absolute inset-x-0 top-1/2 h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent" />
              <div className="relative flex justify-center">
                <span className="bg-slate-900 px-4 text-[10px] font-bold uppercase tracking-[0.3em] text-slate-500">
                  In the audience
                </span>
              </div>
            </div>

            {/* ============ AUDIENCE: Listeners ============ */}
            {listeners.length > 0 && (
              <div className="mb-6">
                <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-7 gap-x-3 gap-y-5">
                  {listeners.map((p) => {
                    const isMe = p.id === user?.id;
                    const hasHandRaised = handRaisedIds.includes(p.id);
                    return (
                      <div key={p.id} className="text-center" data-testid={`listener-${p.id}`}>
                        <div className="relative inline-block mb-1">
                          <div className="w-12 h-12 rounded-full border border-slate-700 bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center shadow">
                            {p.avatar ? (
                              <img src={p.avatar} alt={p.name} className="w-full h-full rounded-full object-cover" />
                            ) : (
                              <span className="text-slate-300 text-sm font-bold">
                                {(p.name || '?').charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                          {hasHandRaised && (
                            <div className="absolute -top-1 -right-1 w-6 h-6 bg-amber-500 rounded-full border-2 border-slate-900 flex items-center justify-center animate-bounce shadow-lg shadow-amber-500/50">
                              <Hand className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                        <p className="text-slate-200 font-medium text-[11px] leading-tight truncate max-w-[80px] mx-auto">{p.name}{isMe ? ' (You)' : ''}</p>
                        {isHost && !isMe && (
                          <div className="flex items-center justify-center gap-1 mt-1 flex-wrap">
                            {hasHandRaised && (
                              <button onClick={() => handleInviteSpeaker(p.id)}
                                className="text-[9px] bg-indigo-600 hover:bg-indigo-700 text-white px-1.5 py-0.5 rounded-full font-semibold"
                                data-testid={`invite-${p.id}`}>
                                <UserPlus className="w-2.5 h-2.5 inline" /> Invite
                              </button>
                            )}
                            <button onClick={() => { if (window.confirm(`Remove ${p.name} from the room?`)) kickParticipant(p.id); }}
                              className="text-[9px] bg-red-600 hover:bg-red-700 text-white px-1.5 py-0.5 rounded-full font-semibold"
                              data-testid={`kick-listener-${p.id}`}>
                              <PhoneOff className="w-2.5 h-2.5 inline" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Audio Visualizer */}
            <div className="mb-8 flex items-center justify-center gap-1">
              {[...Array(20)].map((_, i) => (
                <div key={i} className="w-1.5 bg-gradient-to-t from-indigo-600 to-cyan-400 rounded-full"
                  style={{ height: `${Math.random() * 40 + 10}px`, animation: `audioBar 0.5s ease-in-out ${i * 0.05}s infinite alternate` }} />
              ))}
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-3 sm:gap-4 flex-wrap">
              {/* Mic Toggle */}
              <button data-testid="audio-room-mic-btn"
                className={`group flex flex-col items-center gap-1 transition-transform active:scale-95 ${!isAudioEnabled ? 'opacity-80' : ''}`}
                onClick={() => { toggleAudio(); toast.info(isAudioEnabled ? 'Muted' : 'Unmuted'); }}>
                <div className={`rounded-full w-14 h-14 flex items-center justify-center shadow-lg ${isAudioEnabled ? 'bg-emerald-600 group-hover:bg-emerald-500 shadow-emerald-500/30' : 'bg-slate-700 group-hover:bg-slate-600 border border-slate-600'}`}>
                  {isAudioEnabled ? <Mic className="w-6 h-6 text-white" /> : <MicOff className="w-6 h-6 text-slate-300" />}
                </div>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{isAudioEnabled ? 'Mute' : 'Unmute'}</span>
              </button>

              {/* Raise Hand (non-host only) — featured, labeled */}
              {!isHost && (
                <button data-testid="raise-hand-btn"
                  className="group flex flex-col items-center gap-1 transition-transform active:scale-95"
                  onClick={handleRaiseHand}>
                  <div className={`rounded-full w-16 h-16 flex items-center justify-center shadow-lg ${handRaisedIds.includes(user?.id) ? 'bg-amber-400 ring-4 ring-amber-300/50 animate-pulse shadow-amber-500/40' : 'bg-amber-500 group-hover:bg-amber-400 shadow-amber-500/40'}`}>
                    <Hand className="w-7 h-7 text-white" />
                  </div>
                  <span className="text-[10px] font-semibold text-amber-300 uppercase tracking-wider">
                    {handRaisedIds.includes(user?.id) ? 'Hand up' : 'Raise hand'}
                  </span>
                </button>
              )}

              {/* End Room (host only) */}
              {isHost && (
                <button data-testid="end-room-btn"
                  className="group flex flex-col items-center gap-1 transition-transform active:scale-95"
                  onClick={() => { if (window.confirm('End this room for all participants?')) handleEndRoom(); }}>
                  <div className="rounded-full w-14 h-14 flex items-center justify-center bg-red-700 group-hover:bg-red-600 shadow-lg shadow-red-500/30 border-2 border-red-500">
                    <PhoneOff className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-[10px] font-semibold text-red-400 uppercase tracking-wider">End room</span>
                </button>
              )}

              {/* Leave (all users) */}
              <button data-testid="leave-room-btn"
                className="group flex flex-col items-center gap-1 transition-transform active:scale-95"
                onClick={handleLeaveRoom}>
                <div className={`rounded-full w-14 h-14 flex items-center justify-center shadow-lg ${isHost ? 'bg-slate-700 group-hover:bg-slate-600 border border-slate-500' : 'bg-red-600 group-hover:bg-red-500 shadow-red-500/30'}`}>
                  <PhoneOff className="w-6 h-6 text-white" />
                </div>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Leave</span>
              </button>
            </div>

            <p className="text-center text-sm text-slate-500 mt-4">
              {isAudioEnabled ? 'You are live! Everyone can hear you.' : 'You are muted. Tap the microphone to speak.'}
            </p>
          </div>

          {/* Hidden audio elements — renders remote WebRTC audio streams */}
          {Object.entries(remoteStreams || {}).map(([peerId, stream]) => (
            <AudioPlayer key={peerId} stream={stream} />
          ))}
        </div>
        <style>{`@keyframes audioBar { from { height: 10px; } to { height: 40px; } }`}</style>
      </div>
    );
  }

  // === ROOMS LIST ===
  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar />
      <SessionEndedModal
        open={!!sessionEnded}
        message={sessionEnded?.message}
        endedBy={sessionEnded?.endedBy}
        onClose={() => setSessionEnded(null)}
        variant="audio"
        rateContentType={user?.role === 'student' ? 'audio_room' : undefined}
        rateContentId={sessionEnded?.roomId}
        rateTeacherId={sessionEnded?.hostId}
      />

      {/* Lobby overlay — host hasn't started yet. Polls every 3s and auto-admits. */}
      {lobbyRoom && (
        <div className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-4" data-testid="audio-room-lobby">
          <div className="w-full max-w-md bg-gradient-to-br from-indigo-950 via-slate-900 to-purple-950 border border-indigo-800/60 rounded-2xl p-6 sm:p-8 shadow-2xl text-center">
            <div className="relative w-20 h-20 mx-auto mb-4">
              <div className="absolute inset-0 rounded-full bg-indigo-500/20 animate-ping" />
              <div className="absolute inset-2 rounded-full bg-indigo-500/30 animate-ping" style={{ animationDelay: '0.3s' }} />
              <div className="relative w-full h-full rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                <Headphones className="w-9 h-9 text-white" />
              </div>
            </div>
            <h2 className="text-xl sm:text-2xl font-black text-white mb-2" data-testid="lobby-title">{lobbyRoom.title}</h2>
            <p className="text-sm text-indigo-200 mb-1">
              Waiting for <span className="font-semibold text-white">{lobbyRoom.host_name || 'the host'}</span>
            </p>
            <p className="text-xs text-slate-400 mb-5">
              You'll be admitted automatically the moment the room goes live.
            </p>
            {typeof lobbyRoom.lobby_count === 'number' && lobbyRoom.lobby_count > 1 && (
              <div className="inline-flex items-center gap-1.5 text-xs text-indigo-200 bg-indigo-900/60 border border-indigo-700/60 rounded-full px-3 py-1 mb-4" data-testid="lobby-waiter-count">
                <Users className="w-3.5 h-3.5" />
                <span>{lobbyRoom.lobby_count} waiting</span>
              </div>
            )}
            <div className="flex items-center justify-center gap-2 text-xs text-slate-400 mb-5">
              <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />
              <span>Checking for the host every few seconds…</span>
            </div>
            <Button
              onClick={leaveLobby}
              variant="outline"
              className="w-full bg-slate-800/70 border-slate-600 text-slate-200 hover:bg-slate-700"
              data-testid="lobby-leave-btn"
            >
              Leave Lobby
            </Button>
          </div>
        </div>
      )}

      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/50 via-slate-900 to-blue-900/50" />
        <div className="relative max-w-5xl mx-auto px-4 py-10 sm:py-16 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
              <Headphones className="w-6 h-6 text-white" />
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-white mb-3">Audio Rooms</h1>
          <p className="text-base text-slate-400 max-w-xl mx-auto">
            Join live audio discussions with teachers and students
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-5xl mx-auto px-4 mt-4">
        <div className="flex gap-2 filter-pills-row">
          {filters.map(f => {
            const Icon = f.icon;
            return (
              <button key={f.id} onClick={() => setActiveFilter(f.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${activeFilter === f.id ? 'bg-white text-slate-900' : 'bg-slate-800 text-slate-400 hover:text-white border border-slate-700'}`}
                data-testid={`filter-${f.id}`}>
                <Icon className="w-4 h-4" /> {f.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-10">
        {/* Search Bar */}
        <div className="relative">
          <Headphones className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <input
            type="text"
            placeholder="Search audio rooms by title, subject, or host..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-slate-700 rounded-xl bg-slate-800/50 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            data-testid="audio-rooms-search"
          />
        </div>

        {loading && (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
          </div>
        )}

        {!loading && rooms.length === 0 && (
          <div className="text-center py-20">
            <Radio className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No rooms found</h3>
            <p className="text-slate-400">Check back later for live discussions</p>
          </div>
        )}

        {/* Live Rooms */}
        {liveRooms.length > 0 && (
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <h2 className="text-lg font-bold text-white">Live Now</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {liveRooms.map(room => (
                <div
                  key={room.id}
                  id={`room-${room.id}`}
                  className={`transition-all duration-500 ${focusedRoomId === room.id ? 'ring-4 ring-amber-400 ring-offset-2 ring-offset-slate-900 rounded-2xl scale-[1.02]' : ''}`}
                >
                  <RoomCard room={room} user={user}
                    joiningRoomId={joiningRoomId}
                    onStart={handleStartRoom} onJoin={handleJoinRoom} onRescheduleTomorrow={handleRescheduleTomorrow} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Scheduled */}
        {scheduledRooms.length > 0 && (
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Calendar className="w-5 h-5 text-blue-400" />
              <h2 className="text-lg font-bold text-white">Scheduled</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {scheduledRooms.map(room => (
                <div
                  key={room.id}
                  id={`room-${room.id}`}
                  className={`transition-all duration-500 ${focusedRoomId === room.id ? 'ring-4 ring-amber-400 ring-offset-2 ring-offset-slate-900 rounded-2xl scale-[1.02]' : ''}`}
                >
                  <RoomCard room={room} user={user}
                    joiningRoomId={joiningRoomId}
                    onStart={handleStartRoom} onJoin={handleJoinRoom} onRescheduleTomorrow={handleRescheduleTomorrow} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* To be rescheduled — host didn't start in time. Shown so users
            (and the host) know the room exists and is awaiting a new date. */}
        {rescheduledRooms.length > 0 && (
          <section data-testid="rescheduled-rooms-section">
            <div className="flex items-center gap-3 mb-4">
              <Calendar className="w-5 h-5 text-amber-400" />
              <h2 className="text-lg font-bold text-amber-300">To be Rescheduled</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {rescheduledRooms.map(room => (
                <div
                  key={room.id}
                  id={`room-${room.id}`}
                  className={`transition-all duration-500 ${focusedRoomId === room.id ? 'ring-4 ring-amber-400 ring-offset-2 ring-offset-slate-900 rounded-2xl scale-[1.02]' : ''}`}
                >
                  <RoomCard room={room} user={user}
                    joiningRoomId={joiningRoomId}
                    onStart={handleStartRoom} onJoin={handleJoinRoom} onRescheduleTomorrow={handleRescheduleTomorrow} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Recently completed — kept visible for 3 days (server-side filter)
            so participants can find the recording / verify they attended. */}
        {completedRooms.length > 0 && (
          <section data-testid="completed-rooms-section">
            <div className="flex items-center gap-3 mb-4">
              <Clock className="w-5 h-5 text-slate-400" />
              <h2 className="text-lg font-bold text-slate-300">Recently Ended</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {completedRooms.map(room => (
                <div
                  key={room.id}
                  id={`room-${room.id}`}
                  className={`transition-all duration-500 ${focusedRoomId === room.id ? 'ring-4 ring-amber-400 ring-offset-2 ring-offset-slate-900 rounded-2xl scale-[1.02]' : ''}`}
                >
                  <RoomCard room={room} user={user}
                    joiningRoomId={joiningRoomId}
                    onStart={handleStartRoom} onJoin={handleJoinRoom} onRescheduleTomorrow={handleRescheduleTomorrow} />
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

// Room Card Component
function RoomCard({ room, user, joiningRoomId, onStart, onJoin, onRescheduleTomorrow }) {
  const isHost = user && room.host_id === user.id;
  const isLive = room.status === 'live';
  const isScheduled = room.status === 'scheduled';
  const isCompleted = room.status === 'completed' || room.status === 'ended';
  const needsReschedule = room.status === 'to_be_rescheduled';
  const participantCount = room.participants?.length || 0;

  // Countdown timer for scheduled rooms
  const [countdown, setCountdown] = useState(null);
  useEffect(() => {
    if (!isScheduled || !room.scheduled_date || !room.scheduled_time) { setCountdown(null); return; }
    const tick = () => {
      const scheduled = parseEAT(room.scheduled_date, room.scheduled_time);
      if (!scheduled) { setCountdown(null); return; }
      const diff = scheduled - new Date();
      const twoHrs = 2 * 60 * 60 * 1000;
      if (diff <= 0) setCountdown({ h: 0, m: 0, s: 0, ready: true });
      else if (diff <= twoHrs) {
        setCountdown({ h: Math.floor(diff / 3600000), m: Math.floor((diff % 3600000) / 60000), s: Math.floor((diff % 60000) / 1000), ready: false });
      } else setCountdown(null);
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [isScheduled, room.scheduled_date, room.scheduled_time]);

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl sm:rounded-2xl p-2.5 sm:p-5 hover:border-slate-600 transition-all"
      data-testid={`room-card-${room.id}`}>
      {/* Mobile compact view */}
      <div className="sm:hidden">
        <div className="relative aspect-[3/4] bg-gradient-to-br from-indigo-900/50 to-purple-900/50 rounded-lg flex items-center justify-center mb-1.5 overflow-hidden">
          <Headphones className="w-8 h-8 text-indigo-400/50" />
          {isLive && <span className="absolute top-1 left-1 bg-red-500 px-1 py-0.5 rounded text-[7px] font-bold text-white animate-pulse">LIVE</span>}
          {isScheduled && <span className="absolute top-1 left-1 bg-blue-500 px-1 py-0.5 rounded text-[7px] font-bold text-white">SOON</span>}
          {isCompleted && <span className="absolute top-1 left-1 bg-slate-500 px-1 py-0.5 rounded text-[7px] font-bold text-white">DONE</span>}
          {needsReschedule && <span className="absolute top-1 left-1 bg-amber-500 px-1 py-0.5 rounded text-[7px] font-bold text-white">RESCHED.</span>}
        </div>
        <h3 className="text-white font-semibold text-[11px] line-clamp-2 leading-tight mb-0.5">{room.title}</h3>
        <p className="text-[9px] text-slate-400 line-clamp-1">{room.host_name || 'Host'}</p>
      </div>

      {/* Desktop detailed view */}
      <div className="hidden sm:block">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {isLive && <span className="bg-red-500 px-2 py-0.5 rounded-full text-[10px] font-bold text-white animate-pulse">LIVE</span>}
          {isCompleted && <span className="bg-slate-500/80 px-2 py-0.5 rounded-full text-[10px] font-bold text-white">COMPLETED</span>}
          {needsReschedule && <span className="bg-amber-500 px-2 py-0.5 rounded-full text-[10px] font-bold text-white">TO BE RESCHEDULED</span>}
          <span className="text-xs text-slate-400 capitalize bg-slate-700/50 px-2 py-0.5 rounded-full">{room.category}</span>
        </div>
        {isLive && <span className="text-xs text-slate-400"><Users className="w-3 h-3 inline mr-1" />{participantCount}</span>}
      </div>

      <div className="flex items-start justify-between gap-2 mb-1">
        <h3 className="text-white font-bold text-base flex-1">{room.title}</h3>
        <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
          <ShareButton
            title={room.title}
            path={`/audio-rooms?focus=${room.id}`}
            prefix={isLive ? 'Live audio room' : 'Audio room'}
            testId={`share-room-${room.id}`}
            className="!h-7 !w-7 !bg-white/10 !border-white/20 !text-white hover:!bg-white/20"
          />
        </div>
      </div>
      <p className="text-sm text-slate-400 mb-1 line-clamp-2">{room.topic || room.description}</p>
      {room.topic_explanation && (
        <p className="text-xs text-slate-500 italic mb-3 line-clamp-1">{room.topic_explanation}</p>
      )}
      {!room.topic_explanation && <div className="mb-2" />}

      {(room.level || room.subject) && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {room.level && <span className="bg-indigo-500/20 text-indigo-300 px-2 py-0.5 rounded text-[10px] font-semibold">{room.level}</span>}
          {room.subject && <span className="bg-teal-500/20 text-teal-300 px-2 py-0.5 rounded text-[10px] font-semibold">{room.subject}</span>}
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 rounded-full bg-indigo-900 flex items-center justify-center border border-indigo-600">
          <User className="w-4 h-4 text-indigo-400" />
        </div>
        <span className="text-sm text-slate-300">{room.host_name || 'Host'}</span>
        {isHost && <span className="text-[10px] bg-indigo-500/20 text-indigo-400 px-2 py-0.5 rounded-full font-semibold">You</span>}
      </div>

      {(room.scheduled_date || room.date) && (
        <div className="flex items-center gap-2 mb-3 text-xs text-slate-400">
          <Calendar className="w-3.5 h-3.5" />
          <span>{room.scheduled_date || room.date}{room.scheduled_time ? ` at ${room.scheduled_time}` : room.time ? ` at ${room.time}` : ''}</span>
        </div>
      )}

      {/* Countdown for scheduled rooms */}
      {isScheduled && countdown && !countdown.ready && (
        <div className="mb-3 p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/20" data-testid={`room-countdown-${room.id}`}>
          <p className="text-[10px] text-indigo-400 font-semibold uppercase text-center mb-2">Live Audio Room Starts in</p>
          <div className="flex justify-center gap-2">
            {[{ v: countdown.h, l: 'HR' }, { v: countdown.m, l: 'MIN' }, { v: countdown.s, l: 'SEC' }].map((u, i) => (
              <div key={i} className="text-center">
                <div className="w-11 h-11 rounded-lg bg-indigo-500/15 border border-indigo-500/25 flex items-center justify-center">
                  <span className="text-lg font-black text-indigo-300 tabular-nums">{String(u.v ?? 0).padStart(2, '0')}</span>
                </div>
                <p className="text-[8px] text-indigo-500/60 font-bold mt-1">{u.l}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {isScheduled && countdown?.ready && (
        <div className="mb-3 p-2 rounded-xl bg-green-500/10 border border-green-500/20 text-center">
          <p className="text-sm font-bold text-green-400">Room is ready!</p>
        </div>
      )}

      {/* Action Button */}
      {isLive && !isHost && (
        <Button className="w-full rounded-full bg-white text-slate-900 hover:bg-slate-100 font-bold h-11"
          onClick={() => onJoin(room)} disabled={joiningRoomId !== null}
          data-testid={`join-room-${room.id}`}>
          {joiningRoomId === room.id ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Joining...</> : <><Headphones className="w-4 h-4 mr-2" />Join Room</>}
        </Button>
      )}
      {isLive && isHost && (
        <Button className="w-full rounded-full bg-green-600 hover:bg-green-700 text-white font-bold h-11"
          onClick={() => onJoin(room)} disabled={joiningRoomId !== null}
          data-testid={`rejoin-room-${room.id}`}>
          {joiningRoomId === room.id ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Joining...</> : <><Mic className="w-4 h-4 mr-2" />Rejoin Room</>}
        </Button>
      )}
      {isScheduled && isHost && (
        <Button className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold h-11"
          onClick={() => onStart(room)} disabled={joiningRoomId !== null}
          data-testid={`start-room-${room.id}`}>
          {joiningRoomId === room.id ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Starting...</> : <><Play className="w-4 h-4 mr-2 fill-current" />Start Room</>}
        </Button>
      )}
      {isScheduled && !isHost && (
        <div className="text-center py-2 space-y-2">
          <div className="text-sm text-slate-500">
            {countdown && !countdown.ready ? (
              <span><Clock className="w-4 h-4 inline mr-1" /> Starting soon...</span>
            ) : countdown?.ready ? (
              <span className="text-green-400"><Clock className="w-4 h-4 inline mr-1" /> Waiting for host to start</span>
            ) : (
              <span><Clock className="w-4 h-4 inline mr-1" /> Scheduled</span>
            )}
          </div>
          <Button className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold h-11"
            onClick={() => onJoin(room)} disabled={joiningRoomId !== null}
            data-testid={`join-lobby-${room.id}`}>
            {joiningRoomId === room.id ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Entering…</> : <><Headphones className="w-4 h-4 mr-2" />Wait in Lobby</>}
          </Button>
          <a href={`${API}/reminders/calendar/audio_room/${room.id}`} download
            className="inline-flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 font-medium">
            <Calendar className="w-3 h-3" /> Add to Calendar
          </a>
        </div>
      )}
      {isCompleted && (
        <div className="text-center py-3 bg-slate-700/30 rounded-xl border border-slate-700/50">
          <p className="text-sm text-slate-400 font-medium">
            <Clock className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            This room has ended
          </p>
        </div>
      )}
      {needsReschedule && (
        <div className="text-center py-3 bg-amber-500/10 rounded-xl border border-amber-500/25" data-testid={`room-reschedule-${room.id}`}>
          <p className="text-sm text-amber-300 font-medium">
            <Calendar className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Awaiting reschedule
          </p>
          {isHost || user?.role === 'admin' ? (
            <Button
              size="sm"
              className="mt-2 bg-amber-500 hover:bg-amber-600 text-white rounded-full"
              data-testid={`reschedule-tomorrow-${room.id}`}
              onClick={() => onRescheduleTomorrow && onRescheduleTomorrow(room)}
            >
              <Calendar className="w-3.5 h-3.5 mr-1.5" /> Reschedule for tomorrow
            </Button>
          ) : (
            <p className="text-[10px] text-amber-200/60 mt-0.5">The host will set a new time soon</p>
          )}
        </div>
      )}
      {/* Red progress bar for live audio rooms */}
      {isLive && (() => {
        const now = new Date();
        const ds = room.scheduled_date || room.date || '';
        const ts = room.scheduled_time || room.time || '00:00';
        let start = ds && ts ? new Date(`${ds}T${ts}`) : ds ? new Date(ds) : null;
        const dur = (room.duration_minutes || room.duration || 60) * 60 * 1000;
        const pct = start && !isNaN(start.getTime()) ? Math.min(98, Math.max(2, ((now - start) / dur) * 100)) : 5;
        return (
          <div className="mt-3 h-[3px] bg-red-900/30 rounded-full overflow-hidden">
            <div className="h-full bg-red-500 rounded-full shadow-[0_0_8px_rgba(239,68,68,0.6)]" style={{ width: `${pct}%` }} />
          </div>
        );
      })()}
      </div>
    </div>
  );
}

export default AudioRooms;
